import React, { useState, forwardRef, useImperativeHandle } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { Popover } from 'react-tiny-popover';
import PropTypes from 'prop-types';
import Input from '../../../../../components/Form/Input';
import Select from '../../../../../components/Form/Select';
import { states, employmentStatus } from '../../../../../utils/selectOptions';
import { maskCurrency, matchName, unmaskCurrency, maskPhoneNumber, matchPhoneNumber, matchEmail, maskZipCodes, matchZipCodes, maskDateOfBirth, unmaskNumber, maskSSN3, matchSSN3, unmaskSSN } from '../../../../../utils/masks';

import VerifyAddress, { Popup } from '../../../../../components/VerifyAddress';
import { verifyAddress } from '../../../../../utils/verifyAddress';
import { unSupportedStates } from '../../../../../utils/unSupportedStates';

let callSmartyOnSubmit = false;
let isSSNUpdated = false;

const RevisionForm = forwardRef(({ hookForm, defaultValues }, ref) => {
  const { getValues, clearErrors, setValue, setError } = hookForm;
  const [isVAVisible, setVAVisible] = useState(false); // verify address
  const [isVAVerified, setVAVerified] = useState(false);
  const [isVALoading, setVALoading] = useState(false);
  const [popupType, setPopupType] = useState(0);
  const [isAddressPopupVisible, setPopupVisible] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isPhonePopoverOpen, setIsPhonePopoverOpen] = useState(false);

  const verifySmartAddress = () => {
    const zipcode = getValues('zipCode');
    const address = getValues('streetAddress');
    const city = getValues('city');
    const state = getValues('state');
    console.log('verify started', zipcode, address, city, state);
    setVAVerified(false);
    setVALoading(true);

    verifyAddress({ zipcode, address, city, state })
      .then((response) => {
        console.log('response', response);
        setVALoading(false);
        if (response) {
          if (_.get(response, 'metadata.recordType') === 'P') {
            setVAVerified(false);
            setPopupVisible(true);
            setPopupType(3);
            setError('address', { message: 'Address is not valid. Please enter valid address.' });
            callSmartyOnSubmit = false;
          } else {
            const addressData = {
              address: response.deliveryLine1,
              city: _.get(response, 'components.cityName'),
              state: _.get(response, 'components.state'),
              zipcode: `${response.deliveryPointBarcode}`.slice(0, 5),
            };
            console.log('success', addressData);
            setVAVerified(true);
            clearErrors('streetAddress');
            clearErrors('zipCode');
            clearErrors('city');
            clearErrors('state');
            setValue('streetAddress', addressData.address);
            setValue('city', addressData.city);
            setValue('state', addressData.state);
            setValue('zipCode', addressData.zipcode);
            if (callSmartyOnSubmit) {
              callSmartyOnSubmit = false;
            // doSubmit();
            }
          }
        } else {
          setVAVerified(false);
          setPopupVisible(true);
          setPopupType(2);
          setError('streetAddress', { message: 'Address is not valid. Please enter valid address.' });
          callSmartyOnSubmit = false;
        }
      }).catch((error) => {
        console.log('verify address error', error);
        setVAVerified(false);
        setPopupVisible(true);
        setPopupType(1);
        setVALoading(false);
        setError('streetAddress', { message: 'Address is not valid. Please enter valid address.' });
        callSmartyOnSubmit = false;
      });
  };

  useImperativeHandle(ref, () => ({
    needVerifySmartyAddress() {
      return isVAVisible && !isVAVerified;
    },
    verifySmartyAddress() {
      callSmartyOnSubmit = true;
      verifySmartAddress();
    },
  }));

  const validateAmount = (val) => {
    const value = unmaskCurrency(val);
    if (!value) {
      return 'This input field is required';
    } if (value < 1000 || value > 47500) {
      return 'The amount must be between $1,000 and $47,500';
    }
    return null;
  };

  const validateEmail = (email) => {
    if (!email) {
      return 'Email is required';
    } if (!matchEmail(email)) {
      return 'Email is invalid';
    }
    return null;
  };

  const validatePhoneNumber = (value) => {
    if (!value) {
      return 'This input field is required';
    } if (!matchPhoneNumber.test(value)) {
      return 'Invalid phone number';
    }
    return null;
  };

  const validateZipcode = (value) => {
    if (!value) {
      return 'This input field is required';
    } if (!matchZipCodes.test(value)) {
      return 'Invalid zipCode';
    }
    return null;
  };

  const handleChange = () => {
    clearErrors('address');
    setVAVisible(true);
    setVAVerified(false);
    setVALoading(false);
  };

  const handleUndo = (e) => {
    e.preventDefault();
    setVAVerified(false);
    setVALoading(false);
  };

  const handleAbort = () => {
    setPopupVisible(false);
  };

  const handleVerifyAddressClick = (e) => {
    if (e) {
      e.preventDefault();
    }
    const address = getValues('streetAddress');
    const state = getValues('state');
    if (unSupportedStates.indexOf(state) !== -1 && state !== 'NY') {
      setPopupVisible(true);
      setPopupType(4);
      setVAVerified(false);
      setVALoading(false);
      setError('streetAddress', { message: 'Address is not valid. Please enter valid address.' });
      callSmartyOnSubmit = false;
    } else if (!address) {
      setPopupVisible(true);
      setPopupType(1);
      setError('streetAddress', { message: 'Address is not valid. Please enter valid address.' });
      callSmartyOnSubmit = false;
    } else {
      verifySmartAddress();
    }
  };

  const validateDate = (val) => {
    const realValue = unmaskNumber(val);
    if (!realValue) {
      return 'Required';
    }

    if (realValue.length < 8) {
      return 'Date of Birth is not valid';
    }

    if (!moment(val, 'MM/DD/YYYY').isValid()) {
      return 'Date of Birth is not valid';
    }

    if (moment().diff(moment(val, 'MM-DD-YYYY'), 'years') < 18) {
      return 'Must be over 18 years old';
    }

    if (parseInt(val.slice(-4), 10) < 1900) {
      return 'Please confirm date of birth';
    }
    return null;
  };

  const handleDateChange = (e) => {
    const changedValue = e.target.value;
    const realValue = unmaskNumber(changedValue);
    if (realValue.length >= 8) {
      document.getElementById('SSN1').focus();
    }
  };

  const validateSSN = (name, val) => {
    const realValue = unmaskSSN(val);
    let match = null;
    switch (name) {
      case 'SSN1':
        match = /^[\d*]{3}$/;
        break;
      case 'SSN2':
        match = /^[\d*]{2}$/;
        break;
      case 'SSN3':
        match = matchSSN3;
        break;
      default:
        match = /^[\d*]{3}$/;
        break;
    }
    if (!realValue) {
      return 'Required';
    } if (!match.test(realValue)) {
      return 'Invalid SSN';
    }
    if (name === 'SSN1') {
      if (defaultValues.SSN3 !== getValues('SSN3') || defaultValues.SSN2 !== getValues('SSN2')) {
        if (val.indexOf('*') >= 0) {
          return 'Invalid SSN';
        }
      }
    }
    if (name === 'SSN2') {
      if (defaultValues.SSN3 !== getValues('SSN3') || defaultValues.SSN1 !== getValues('SSN1')) {
        if (val.indexOf('*') >= 0) {
          return 'Invalid SSN';
        }
      }
    }
    return null;
  };

  const validateState = (val) => {
    if (unSupportedStates.indexOf(val) !== -1) {
      return 'Unsupported State';
    }
    return null;
  };

  const handleSSNChange = (name, e) => {
    if (!isSSNUpdated && (defaultValues.ssn && defaultValues.ssn.indexOf('*') >= 0)) {
      isSSNUpdated = true;
      setValue('SSN1', '');
      setValue('SSN2', '');
      setValue('SSN3', '');
      return;
    }
    const changedValue = e.target.value;
    const realValue = unmaskSSN(changedValue);
    switch (name) {
      case 'SSN1':
        if (realValue.length >= 3) {
          document.getElementById('SSN2').focus();
        }
        break;
      case 'SSN2':
        if (realValue.length >= 2) {
          document.getElementById('SSN3').focus();
        }
        break;
      default:
        break;
    }
  };

  const validateGrossAmount = (val) => {
    const value = unmaskCurrency(val);
    if (!value) {
      return 'This input field is required';
    } if (value < 0 || value > 41600) {
      return 'The amount must be between $0 and $41,600';
    }
    return null;
  };

  const validateEmployerName = (name) => {
    const status = getValues('employmentStatus');
    if (status === 'unemployed' || status === 'student' || status === 'retired') {
      return null;
    }
    if (!name) {
      return 'Employer Name is required';
    } if (!matchName(name)) {
      return `${name} is not a valid name.`;
    }
    return null;
  };

  const validateName = (name) => {
    if (!name) {
      return 'Name is required';
    } if (!matchName(name)) {
      return `${name} is not a valid name.`;
    }
    return null;
  };

  const handleStatusChange = () => {
    clearErrors('employerName');
    clearErrors('employmentStatus');
  };

  return (
    <div className="w-[95%] pt-[20px] pb-[30px]">
      <div className="flex w-full">
        <Input
          autoFocus
          name="requestedAmount"
          label="Loan Amount"
          placeholder="$ "
          className="w-full md:w-2/3"
          isMasked={maskCurrency}
          hookForm={hookForm}
          validate={validateAmount}
        />
      </div>
      <div className="flex flex-col md:flex-row justify-between">
        <Input
          name="email"
          label="Email Address"
          className="w-full md:w-[47%]"
          isMasked={false}
          hookForm={hookForm}
          registerOptions={{
            required: 'Email is required',
            validate: validateEmail,
          }}
        />
        <Input
          name="phoneNumber"
          label="Phone Number"
          className="w-full md:w-[47%]"
          isMasked={maskPhoneNumber}
          hookForm={hookForm}
          validate={validatePhoneNumber}
        />
      </div>
      <div className="text-left font-medium text-[14px] leading-[16px] text-black mt-[15px]">
        I consent to receive calls and texts for any purpose, including marketing, under these
        <Popover
          isOpen={isPhonePopoverOpen}
          positions={['top']} // preferred positions by priority
          align="start"
          padding={2}
          onClickOutside={() => setIsPhonePopoverOpen(false)}
          content={(
            <>
              <div className="rounded-md border border-[#00214732] p-5 bg-[#ffffff] m-auto max-w-[90%] md:max-w-[400px] max-h-[500px] overflow-y-auto text-[#3f556e] text-[14px] scrollbar">
                <strong>Terms</strong>
                <br />
                <br />
                By providing the telephone number above and clicking &quot;SUBMIT NOW&quot;, you confirm you are the owner of the phone number(s), or that the owner authorized you to provide the phone number(s), or that you are the regular user of this phone number(s). You are providing express written consent via your electronic signature to receive any type of communication for any purpose, including telemarketing communications, from our
                {' '}
                <a className=" underline text-[#0000ff] " href="https://lendingusa.com/lending-partners/" target="_blank" rel="noreferrer">lending partners</a>
                ,   LendingUSA, their agents, representatives, affiliates, assignees or anyone calling on their behalf, at the telephone number(s) provided for any lawful purpose, even if that number is on a federal or state do-not-call list. The scope of this consent includes, but is not limited to, receiving live operator calls, calls and texts via an automatic telephone dialing system (auto-dialer), prerecorded and artificial voice messages, text/SMS messages or other means. These telephone calls and text messages may incur message, data and access fees from your telephone provider. Consent is not a condition to obtain a loan.
                <br />
                <br />

                You understand that you can request a paper copy of these terms or may withdraw your consent at any time by:
                <ul style={{ listStyle: 'unset', marginLeft: '10px', paddingLeft: '10px' }}>
                  <li>mailing your written request to LendingUSA c/o Compliance Department at 15303 Ventura Blvd., Suite 850, Sherman Oaks, CA 91403;</li>
                  <li>
                    by e-mailing
                    <a className=" underline text-[#0000ff] " href="mailto:customercare@lendingusa.com">customercare@lendingusa.com</a>
                    ; or
                  </li>
                  <li>by calling LendingUSA at (800) 994-6177.</li>
                </ul>
                <br />
                You may also reply “STOP” to opt out of receiving autodialed text messages only.
              </div>
              <span className="arrow left-2 hidden md:block" />
            </>
          )}
        >
          <span className="pl-[5px] underline cursor-pointer" onClick={() => setIsPhonePopoverOpen(!isPhonePopoverOpen)}>terms</span>
        </Popover>
        .
      </div>
      <div className="h-[40px]" />
      <div className="flex flex-col md:flex-row justify-between">
        <Input
          name="firstName"
          label="First Name"
          className="w-full md:w-[47%]"
          isMasked={false}
          hookForm={hookForm}
          registerOptions={{
            required: 'First Name is required',
            validate: validateName,
            onBlur: (e) => {
              const value = e.target.value || '';
              e.target.value = value.replace(/\w+/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());
            },
          }}
        />
        <Input
          name="lastName"
          label="Last Name"
          className="w-full md:w-[47%]"
          isMasked={false}
          hookForm={hookForm}
          registerOptions={{
            required: 'Last Name is required',
            validate: validateName,
            onBlur: (e) => {
              const value = e.target.value || '';
              e.target.value = value.replace(/\w+/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());
            },
          }}
        />
      </div>
      <div className="h-[40px]" />
      <div className="relative">
        <Input
          name="streetAddress"
          label="Street Address"
          className="w-full"
          isMasked={false}
          hookForm={hookForm}
          registerOptions={{ required: 'Street is required', onChange: handleChange }}
        />
        {isVAVisible
          && (
          <VerifyAddress
            className="absolute right-[4px] top-[39px] md:top-[34px]"
            isVerified={isVAVerified}
            isLoading={isVALoading}
            onClick={isVAVerified ? handleUndo : handleVerifyAddressClick}
          />
          )}
        {isAddressPopupVisible
          && (
          <Popup
            handleAbort={handleAbort}
            type={popupType}
            data={{
              address: getValues('address'),
              city: getValues('city'),
              state: getValues('state'),
              zipcode: getValues('zipCode'),
            }}
          />
          )}
      </div>
      <div className="flex w-full flex-wrap justify-between">
        <div className="w-3/5 md:w-2/5">
          <Input
            name="city"
            label="City"
            isMasked={false}
            hookForm={hookForm}
            registerOptions={{ required: 'City is required', onChange: handleChange }}
          />
        </div>
        <div className="w-1/3 md:w-1/4">
          <Select
            name="state"
            label="State"
            data={states}
            hookForm={hookForm}
            onChange={handleChange}
            registerOptions={{ required: 'State is required', validate: validateState }}
          />
        </div>
        <div className="w-1/4">
          <Input
            name="zipCode"
            label="Zip Code"
            isMasked={maskZipCodes}
            hookForm={hookForm}
            validate={validateZipcode}
            onCustomChange={handleChange}
          />
        </div>
      </div>
      <div className="h-[40px]" />
      <div className="flex flex-col md:flex-row justify-between">
        <Input
          label="Date of Birth"
          name="dateOfBirth"
          className="w-full md:w-[47%]"
          isMasked={maskDateOfBirth}
          placeHolder="__/__/____"
          hookForm={hookForm}
          onCustomChange={handleDateChange}
          validate={validateDate}
        />
        <div className="w-full md:w-[47%]">
          <div className="mt-[18px] text-left font-bold text-[14px] leading-[16px] text-black">
            Social Security Number
          </div>
          <div className="flex">
            <Input
              name="SSN1"
              className="mt-0 w-[70px]"
              label=""
              isMasked={[/[\d*]/, /[\d*]/, /[\d*]/]}
              hookForm={hookForm}
              onCustomChange={handleSSNChange.bind(this, 'SSN1')}
              validate={validateSSN.bind(this, 'SSN1')}
            />
            <Input
              name="SSN2"
              className="mt-0 w-[50px] ml-[10px]"
              label=""
              isMasked={[/[\d*]/, /[\d*]/]}
              hookForm={hookForm}
              onCustomChange={handleSSNChange.bind(this, 'SSN2')}
              validate={validateSSN.bind(this, 'SSN2')}
            />
            <Input
              name="SSN3"
              className="mt-0 w-[100px] ml-[10px]"
              label=""
              isMasked={maskSSN3}
              hookForm={hookForm}
              onCustomChange={handleSSNChange.bind(this, 'SSN3')}
              validate={validateSSN.bind(this, 'SSN3')}
            />
          </div>
        </div>
      </div>
      <div className="h-[40px]" />
      <div className="flex w-full relative">
        <Input
          name="grossMonthlyIncome"
          label="Gross Monthly Income"
          placeholder="$ "
          className="w-full md:w-2/3"
          isMasked={maskCurrency}
          hookForm={hookForm}
          validate={validateGrossAmount}
        />
        <div className="absolute top-[21px]">
          <Popover
            isOpen={isPopoverOpen}
            positions={['top']} // preferred positions by priority
            align="start"
            padding={2}
            onClickOutside={() => setIsPopoverOpen(false)}
            containerClassName="mt-[-8px] ml-[0px] md:ml-[-18px]"
            content={(
              <>
                <div className="rounded-md border border-[#00214732] p-5 bg-[#ffffff] m-auto max-w-[90%] md:max-w-[400px] max-h-[500px] overflow-y-auto text-[#3f556e] text-[14px] scrollbar">
                  Your total verifiable gross income might include your salary, retirement income or other sources of income.
                  <br />
                  You don&apos;t have to include alimony, child support, or separate maintenance income unless you want it considered as a basis for repayment.
                  <br />
                  Your income and/or employment is subject to verification.
                </div>
                <span className="arrow left-2 hidden md:block" />
              </>
            )}
          >
            <div className="absolute top-[-4px] left-[150px] text-center w-[20px] h-[20px] rounded-[20px] bg-[#87cefa] leading-[20px] cursor-pointer" onClick={() => setIsPopoverOpen(!isPopoverOpen)}>?</div>
          </Popover>
        </div>
      </div>
      <div className="flex flex-col md:flex-row justify-between">
        <div className="w-full md:w-2/5">
          <Select
            name="employmentStatus"
            label="Employment Status"
            data={employmentStatus}
            hookForm={hookForm}
            registerOptions={{
              required: 'Employment Status is required',
            }}
            onChange={handleStatusChange}
          />
        </div>
        <div className="ml-[0px] w-full md:w-3/5 md:ml-[10px]">
          <Input
            name="employerName"
            label="Employer Name"
            isMasked={false}
            hookForm={hookForm}
            registerOptions={{
              validate: validateEmployerName,
            }}
          />
        </div>
      </div>
    </div>
  );
});

RevisionForm.propTypes = {
  hookForm: PropTypes.object.isRequired,
  defaultValues: PropTypes.object.isRequired,
};

export default RevisionForm;
